<template>
  <div class="customer-management">
    <div class="header-actions">
      <h2 class="page-title">客户管理</h2>
      <div class="action-btns">
        <button class="btn btn-primary" @click="exportData" :disabled="userList.length === 0">
          <i class="el-icon-download"></i> 导出客户
        </button>
        <button class="btn btn-danger" @click="batchDelete" :disabled="selectedUsers.length === 0">
          <i class="el-icon-delete"></i> 批量删除 ({{ selectedUsers.length }})
        </button>
      </div>
    </div>
    
    <form autocomplete="off" @keydown.enter="save">
      <div class="form-left">
        <div class="form-element">
          <label class="form-label">头像</label>
          <!-- 参考 components/User.vue：有头像走 img；有姓名没头像退化成姓名首字；连姓名都没有才用未登录图 -->
          <img v-if="curUser.profile" style="width: 200px; border-radius: 3px" :src="curUser.profile | handleUrl">
          <span v-else-if="curUser.name" class="name-avatar" :style="{ background: avatarColor(curUser.name) }">{{ avatarInitial(curUser.name) }}</span>
          <img v-else style="width: 200px" src="../assets/imgs/unlogin.svg">
        </div>
        <div class="form-element">
          <label class="form-label required">头像地址</label>
          <el-input type="text" class="input" name="profile_url" placeholder="" v-model="curUser.profile" clearable></el-input>
        </div>
        <div class="form-element">
          <label class="form-label required">客户名</label>
          <el-input v-if="user" ref="name" type="text" class="input" name="username" placeholder="" v-model="curUser.name" clearable></el-input>
        </div>
        <div class="form-element">
          <label class="form-label">手机</label>
          <el-input type="text" class="input" name="phone" placeholder="填写手机号码" value="" v-model="curUser.phone" clearable></el-input>
        </div>
        <div class="form-element">
          <label class="form-label">微信 ID</label>
          <el-input type="text" class="input" name="email" placeholder="微信 ID 可不填" value="" v-model="curUser.wechatId" clearable></el-input>
        </div>
        <div class="form-element">
          <label class="form-label">微信号</label>
          <el-input type="text" class="input" name="email" placeholder="微信号可不填" value="" v-model="curUser.wechatNum" clearable></el-input>
        </div>
        <div class="form-element b10">
          <label class="form-label">微信名</label>
          <el-input type="text" class="input" name="email" placeholder="微信名可不填" value="" v-model="curUser.wechatName" clearable></el-input>
        </div>
        <div class="form-element">
          <label class="form-label">备注</label>
          <el-input type="textarea" class="input textarea" name="remark" placeholder="客户备注信息" v-model="curUser.remark" clearable rows="3"></el-input>
        </div>
        <div class="form-element">
          <label class="form-label">客户标签</label>
          <el-select v-model="curUser.tags" multiple placeholder="选择客户标签" class="tag-select" clearable>
            <el-option label="重要客户" value="important"></el-option>
            <el-option label="普通客户" value="normal"></el-option>
            <el-option label="潜在客户" value="potential"></el-option>
            <el-option label="VIP 客户" value="vip"></el-option>
          </el-select>
        </div>
        <div class="form-element btns active">
          <span class="btn active" @click="save">{{ curUser.id ? '更新客户信息' : '添加新的客户' }}</span>
          <span class="btn" @click="resetForm" v-if="curUser.id">取消编辑</span>
        </div>
      </div>
      
      <div class="form-right">
        <div class="search-box">
          <div class="search-input">
            <label class="form-label">搜索用户</label>
            <el-input 
              type="text" 
              class="input search-input" 
              placeholder="输入客户名、手机号、微信名搜索" 
              v-model="searchKeyword" 
              @input="searchUser" 
              clearable
              prefix-icon="el-icon-search">
            </el-input>
          </div>
          <div class="filter-tags">
            <el-tag 
              v-for="tag in filterTags" 
              :key="tag.value" 
              :type="currentFilter === tag.value ? 'primary' : 'info'"
              effect="plain"
              size="small"
              @click="filterByTag(tag.value)"
              class="filter-tag"
            >
              {{ tag.label }}
            </el-tag>
            <el-tag 
              effect="plain"
              size="small"
              @click="filterByTag('')"
              class="filter-tag"
              :type="currentFilter === '' ? 'primary' : 'info'"
            >
              全部
            </el-tag>
          </div>
        </div>
        
        <div class="list-header">
          <el-checkbox v-model="selectAll" @change="handleSelectAllChange">全选</el-checkbox>
          <span class="total-count">共 {{ total }} 条记录</span>
        </div>
        
        <div class="user-list">
          <div v-for="(user, i) in userList" :key="i" class="user-item" :class="{ selected: selectedUsers.includes(user.id) }">
            <div class="user-item-left" @click="setEditUser(user)">
              <User :user="user" class="user" :show-user-name="false"></User>
              <div class="user-info">
                <div class="user-name">{{ user.name }}</div>
                <div class="user-detail">
                  <span v-if="user.phone"><i class="el-icon-mobile-phone"></i> {{ user.phone }}</span>
                  <span v-if="user.wechatName" class="wechat"><i class="el-icon-chat-dot-square"></i> {{ user.wechatName }}</span>
                </div>
                <div class="user-meta">
                  <el-tag size="mini" v-for="tag in user.tags" :key="tag" class="user-tag">{{ getTagName(tag) }}</el-tag>
                  <span class="create-time" v-if="user.createTime">创建：{{ formatDate(user.createTime) }}</span>
                </div>
              </div>
            </div>
            <div class="user-item-right">
              <el-checkbox v-model="user.selected" @change="handleUserCheck(user)" :checked="selectedUsers.includes(user.id)"></el-checkbox>
              <button type="button" class="btn-edit" @click="setEditUser(user)">编辑</button>
              <button type="button" class="btn-del" @click.stop="deleteUser(user)">删除</button>
            </div>
          </div>
          
          <div v-if="userList.length === 0" class="empty-data">
            <img src="../assets/imgs/unlogin.svg" alt="空数据">
            <p>暂无客户数据</p>
          </div>
        </div>
        
        <div class="pagination" v-if="total > pageSize">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="total"
            :page-size="pageSize"
            :current-page="currentPage"
            @current-change="handlePageChange"
          >
          </el-pagination>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import '../assets/styles/setting-m.css';
import '../assets/styles/setting.css';
import User from "@/components/User";
import util from "@/util";
import { avatarColor, avatarInitial } from '@/utils/avatar';

const handlePaste = e => {
  if (!e.clipboardData || !e.clipboardData.items) {
    return;
  }
  let file = false;
  let i = 0, len = e.clipboardData.items.length;
  for (; i < len; i++) {
    const item = e.clipboardData.items[i];
    if (/image/.test(item.type)) {
      file = item.getAsFile();
    }
  }
  if (file)
    window.$settingCustomer.uploadProfile(file)
}
const newUser = {
  id: null,
  profile: '',
  name: '',
  phone: '',
  wechatId: '',
  wechatNum: '',
  wechatName: '',
  remark: '',
  tags: [],
  createTime: null,
  updateTime: null
}
export default {
  components: {User},

  data () {
    return {
      curUser: util.clone(newUser),
      searchKeyword: '',
      selectAll: false,
      selectedUsers: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      currentFilter: '',
      allUsers: [],
      is: {
        update: false
      },
      userList: [],
      loading: {
        searchUser: false,
        save: false,
        delete: false
      },
      filterTags: [
        { label: '重要客户', value: 'important' },
        { label: '普通客户', value: 'normal' },
        { label: '潜在客户', value: 'potential' },
        { label: 'VIP 客户', value: 'vip' }
      ]
    }
  },
  inject: ['componentRoot'],
  methods: {
    // 无头像兜底：与 components/User.vue 同源（@/utils/avatar），保证同一个人全站同色
    avatarColor,
    avatarInitial,
    searchUser (val) {
      this.loading.searchUser = true
      this.currentPage = 1
      this.$get('/user/list/last-online', {name: val}, res => {
        if (res) {
          this.allUsers = res
          this.total = res.length
          this.filterAndPaginateUsers()
        }
        this.loading.searchUser = false
      })
    },
    filterAndPaginateUsers () {
      let filtered = this.allUsers
      
      // 按标签筛选
      if (this.currentFilter) {
        filtered = filtered.filter(u => u.tags && u.tags.includes(this.currentFilter))
      }
      
      // 分页
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      this.userList = filtered.slice(start, end)
      this.total = filtered.length
    },
    filterByTag (tag) {
      this.currentFilter = tag
      this.currentPage = 1
      this.filterAndPaginateUsers()
    },
    handlePageChange (page) {
      this.currentPage = page
      this.filterAndPaginateUsers()
    },
    handleSelectAllChange (val) {
      if (val) {
        this.selectedUsers = this.userList.map(u => u.id)
        this.userList.forEach(u => u.selected = true)
      } else {
        this.selectedUsers = []
        this.userList.forEach(u => u.selected = false)
      }
    },
    handleUserCheck (user) {
      if (user.selected) {
        if (!this.selectedUsers.includes(user.id)) {
          this.selectedUsers.push(user.id)
        }
      } else {
        this.selectedUsers = this.selectedUsers.filter(id => id !== user.id)
      }
      // 更新全选状态
      this.selectAll = this.selectedUsers.length === this.userList.length && this.userList.length > 0
    },
    batchDelete () {
      if (this.selectedUsers.length === 0) {
        this.$message.warning('请选择要删除的客户')
        return
      }
      
      this.$confirm(`确定要删除选中的 ${this.selectedUsers.length} 个客户吗？删除后无法恢复。`, '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loading.delete = true
        this.$post('/user/batch-delete', {ids: this.selectedUsers}, () => {
          this.loading.delete = false
          this.$message.success('删除成功')
          this.selectedUsers = []
          this.selectAll = false
          this.loadData()
        })
      }).catch(() => {})
    },
    deleteUser (user) {
      this.$confirm(`确定要删除客户 "${user.name}" 吗？删除后无法恢复。`, '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loading.delete = true
        this.$post('/user/delete-by-id', {id: user.id}, () => {
          this.loading.delete = false
          this.$message.success('删除成功')
          if (this.curUser.id === user.id) {
            this.resetForm()
          }
          this.loadData()
        })
      }).catch(() => {})
    },
    resetForm () {
      this.curUser = util.clone(newUser)
      this.$refs.name && this.$refs.name.focus && this.$refs.name.focus()
    },
    setEditUser (user) {
      if (!user) {
        this.curUser = util.clone(newUser)
      } else {
        this.curUser.id = user.id
        this.curUser.name = user.name
        this.curUser.wechatName = user.wechatName
        this.curUser.phone = user.phone
        this.curUser.profile = user.profile
        this.curUser.wechatId = user.wechatId
        this.curUser.wechatNum = user.wechatNum
        this.curUser.remark = user.remark || ''
        this.curUser.tags = user.tags || []
        this.curUser.createTime = user.createTime
        this.curUser.updateTime = user.updateTime
      }
    },
    uploadProfile (file) {
      const doUpload = (f) => {
        this.$uploadOss('/upload', {file: f, userId: this.user ? this.user.id : null}, res => {
          console.log('upload:', res)
          this.curUser.profile = res[0].match(/img\/(.*)$/)[1]
          this.$message.success('头像上传成功')
        })
      }
      // 原图直传 OSS（不压缩，保证质量）
      doUpload(file)
    },
    save () {
      // 验证必填字段
      if (!this.curUser.name) {
        this.$message.warning('请输入客户名')
        this.$refs.name && this.$refs.name.focus()
        return
      }
      
      if (this.loading.save) return
      this.loading.save = true
      
      if(this.curUser.id){
        this.$post('/user/update-by-id', this.curUser, () => {
          this.loading.save = false
          this.$message.success('更新成功')
          this.curUser = util.clone(newUser)
          this.loadData()
        }, () => {
          this.loading.save = false
        })
      } else {
        this.$post('/user/add', this.curUser, () => {
          this.loading.save = false
          this.$message.success('添加成功')
          this.curUser = util.clone(newUser)
          this.loadData()
        }, () => {
          this.loading.save = false
        })
      }
    },
    exportData () {
      if (this.allUsers.length === 0) {
        this.$message.warning('暂无可导出的数据')
        return
      }
      
      // 导出数据为 CSV 格式
      const headers = ['客户名', '手机号', '微信 ID', '微信号', '微信名', '备注', '标签']
      const rows = this.allUsers.map(u => [
        u.name,
        u.phone || '',
        u.wechatId || '',
        u.wechatNum || '',
        u.wechatName || '',
        u.remark || '',
        (u.tags || []).map(t => this.getTagName(t)).join(',')
      ])
      
      // 添加 BOM 防止中文乱码
      const BOM = '\uFEFF'
      const csvContent = [BOM, headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n')
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `客户数据_${new Date().format('YYYYMMDD_HHmmss')}.csv`
      link.click()
      
      this.$message.success('导出成功')
    },
    getTagName (tag) {
      const tagMap = {
        'important': '重要客户',
        'normal': '普通客户',
        'potential': '潜在客户',
        'vip': 'VIP 客户'
      }
      return tagMap[tag] || tag
    },
    formatDate (dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    loadData () {
      this.searchKeyword = ''
      this.currentFilter = ''
      this.currentPage = 1
      this.$get('/user/list/last-online', {}, res => {
        if (res) {
          this.allUsers = res
          this.total = res.length
          this.filterAndPaginateUsers()
        }
      })
    }
  },
  computed: {
    user () {
      return this.$store.state.user;
    }
  },
  watch: {
    user (newUser) {
      this.setField(newUser)
    },
  },
  beforeDestroy () {
    document.removeEventListener('paste', handlePaste)
  },
  mounted () {
    window.$settingCustomer = this
    document.addEventListener('paste', handlePaste)
    this.loadData()
    
    // 添加日期格式化方法
    Date.prototype.format = function(fmt) {
      const o = {
        'M+': this.getMonth() + 1,
        'd+': this.getDate(),
        'H+': this.getHours(),
        'm+': this.getMinutes(),
        's+': this.getSeconds()
      }
      if (/(Y+)/.test(fmt))
        fmt = fmt.replace(RegExp.$1, (this.getFullYear() + '').substr(4 - RegExp.$1.length))
      for (const k in o)
        if (new RegExp('(' + k + ')').test(fmt))
          fmt = fmt.replace(RegExp.$1, (RegExp.$1.length === 1) ? (o[k]) : (('00' + o[k]).substr(('' + o[k]).length)))
      return fmt
    }
  }
}
</script>
<style scoped>
.customer-management {
  padding: 20px;
  background: #fff;
  min-height: calc(100vh - 84px);
}

/* 无头像兜底：与 img 预览同尺寸（200×200、3px 圆角），只补居中与字色 */
.name-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 200px;
  border-radius: 3px;
  color: #fff;
  font-size: 64px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e5e5;
}

.page-title {
  font-size: 24px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.action-btns {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 10px 20px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #409EFF;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #66b1ff;
}

.btn-danger {
  background: #F56C6C;
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  background: #f78989;
}

form {
  display: flex;
  gap: 30px;
}

.form-left {
  flex: 0 0 400px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  padding-right: 20px;
}

.form-right {
  flex: 1;
  min-width: 600px;
}

.search-box {
  margin-bottom: 20px;
}

.search-input {
  margin-bottom: 15px;
}

.search-input .input {
  width: 100%;
  max-width: 500px;
}

.filter-tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-tag {
  cursor: pointer;
  margin-right: 5px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 10px 15px;
  background: #f5f7fa;
  border-radius: 4px;
}

.total-count {
  font-size: 14px;
  color: #909399;
}

.user-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: calc(100vh - 350px);
  overflow-y: auto;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  transition: all 0.3s;
  cursor: pointer;
}

.user-item:hover {
  background-color: #f5f7fa;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
}

.user-item.selected {
  background-color: #ecf5ff;
  border-color: #409EFF;
}

.user-item-left {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 15px;
}

.user-info {
  flex: 1;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
}

.user-detail {
  display: flex;
  gap: 15px;
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.user-detail i {
  margin-right: 3px;
}

.user-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.user-tag {
  margin-right: 5px;
}

.create-time {
  font-size: 12px;
  color: #909399;
}

.user-item-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-edit, .btn-del {
  padding: 5px 12px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s;
}

.btn-edit {
  background: #409EFF;
  color: #fff;
}

.btn-edit:hover {
  background: #66b1ff;
}

.btn-del {
  background: #F56C6C;
  color: #fff;
}

.btn-del:hover {
  background: #f78989;
}

.empty-data {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-data img {
  width: 120px;
  height: 120px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-data p {
  font-size: 14px;
  margin: 0;
}

.pagination {
  margin-top: 20px;
  text-align: center;
}

.tag-select {
  width: 100%;
}

.textarea {
  width: 100%;
  resize: vertical;
}

/* 响应式设计 */
@media screen and (max-width: 1200px) {
  form {
    flex-direction: column;
  }
  
  .form-left {
    flex: 1;
    max-width: 100%;
  }
  
  .form-right {
    min-width: auto;
  }
}

.input {
  padding: unset;
  border: unset;
}
</style>